import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { FiChevronUp } from 'react-icons/fi';
import classNames from 'classnames';
import HotelRoom, { MealPlan } from '../../../../types/RoomAvailability';
import Accordion from '../../common/Accordion/Accordion';
import ImageWithFallback from '../../common/ImageWithFallback';
import Button from '../../common/Button';
import planConfigSlice from '../../../core/planConfig/slice';
import useAppDispatch from '../../../core/redux/useAppDispatch';
import analytics from '../../../core/analytics/analytics';
import { ContentType } from '../../../core/constants';

type Props = {
  category: string;
  mealPlans: MealPlan[];
  isOpen?: boolean;
  hotelRoom: HotelRoom | undefined;
  renderPrice: (mealPlan: MealPlan, textSize: string, hasTilde?: boolean) => React.ReactNode;
  getLinkUrl: (roomId: number, mealPlanId: string) => string;
  setRoomId: (roomId?: number) => void;
};

const MealPlanAccordion = ({
  category,
  mealPlans,
  isOpen = false,
  hotelRoom,
  renderPrice,
  getLinkUrl,
  setRoomId,
}: Props) => {
  const [open, setOpen] = useState(isOpen);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const events = {
    onOpenModal: (mealPlan: MealPlan) => {
      setRoomId(hotelRoom?.id);
      dispatch(
        planConfigSlice.actions.openMealPlanConfigModal({
          targetSelectedMealPlanId: mealPlan?.id as unknown as number,
          newlySelectedMealPlan: mealPlan,
          modalType: 'normal',
        })
      );
      analytics.google.basic.track.select(ContentType.MEAL_PLAN_DETAIL, mealPlan?.id ?? '');
    },
  };

  const button = (
    <div
      className="flex w-full items-center justify-between gap-2 px-3 py-5 md:p-4"
      role="button"
      tabIndex={0}
      onClick={() => setOpen(!open)}
      onKeyDown={() => setOpen(!open)}
    >
      <div>
        <p className="text-sm font-bold">{category}</p>
      </div>
      <div className="flex items-center gap-2 lg:gap-4">
        <div className="flex flex-col items-end">
          <span className="whitespace-nowrap text-micro text-gray-600">
            {t('common:label.total_price_short')}
          </span>
          <span className="whitespace-nowrap text-micro text-gray-600">
            {t('common:label.include_tax')}
          </span>
        </div>
        {mealPlans.length > 0 &&
          renderPrice(mealPlans[0] as MealPlan, 'small', mealPlans.length > 1)}
        <div
          className={classNames('inline-block', {
            'transform duration-700 ease': open,
            'transform duration-700 ease rotate-180': !open,
          })}
        >
          <FiChevronUp size="24" className="text-orange-700" />
        </div>
      </div>
    </div>
  );

  const renderMealPlan = (mealPlan: MealPlan) => (
    <div
      key={mealPlan?.id}
      className="flex flex-col items-center gap-4 border-t border-dotted border-gray-200 p-3 sm:flex-row md:p-4"
    >
      <button
        type="button"
        className="flex w-full grow justify-start gap-2"
        onClick={() => events.onOpenModal(mealPlan)}
      >
        <div className="relative size-18 flex-none">
          <ImageWithFallback
            src={mealPlan?.imageUrl || '/no-plan-selected-desktop.svg'}
            alt="meal-plan-pic-desktop"
            fallbackSrc="/no-plan-selected-desktop.svg"
            layout="fill"
            objectFit="cover"
            className="rounded-md"
          />
        </div>
        <div className="flex flex-col gap-1 text-left">
          <p className="line-clamp-2 text-sm">{mealPlan?.name}</p>
          <p className="w-fit cursor-pointer text-small text-teal-600 hover:underline">
            {t('component:plan_config.card.read_more')}
          </p>
        </div>
      </button>
      <div className="flex w-full items-center justify-between gap-2 md:w-fit">
        <div className="flex w-fit flex-col whitespace-nowrap md:flex-row">
          <span className="text-xs text-gray-600">{t('common:label.total_price_short')}</span>
          <span className="text-xs text-gray-600">{t('common:label.include_tax')}</span>
        </div>
        <div className="flex grow items-center justify-end gap-2 whitespace-nowrap lg:gap-4">
          {renderPrice(mealPlan, 'medium', false)}
          <Button
            color="red"
            size="small"
            className="shrink-0 px-4"
            to={getLinkUrl(hotelRoom?.id as number, mealPlan?.id as string)}
          >
            {t('common:button.select')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Accordion
      open={open}
      key={category}
      button={button}
      borderColor="gray"
      direction="bottom"
      customClassName="first:rounded-b-none first:md:rounded-b border-t-0 md:border-t rounded-t-none md:rounded-t"
      content={mealPlans.map((mealPlan) => renderMealPlan(mealPlan))}
    />
  );
};

export default MealPlanAccordion;
